import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,text-decoration-color,fill,stroke,filter] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-accent text-accent-foreground hover:brightness-110",
        outline: "border border-border bg-surface hover:border-accent hover:bg-accent-soft hover:text-accent",
        ghost: "text-muted-foreground hover:bg-accent-soft hover:text-accent",
        subtle: "bg-surface-elevated text-foreground hover:bg-accent-soft hover:text-accent"
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-9 px-4",
        icon: "h-8 w-8"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "md"
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const runtimeEnvironment = (
  import.meta as ImportMeta & {
    readonly env?: { readonly DEV?: boolean; readonly MODE?: string };
  }
).env;

const shouldValidateAccessibility =
  runtimeEnvironment?.DEV === true || runtimeEnvironment?.MODE === "test";

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isHiddenFromAccessibility(element: Element): boolean {
  return element.getAttribute("aria-hidden") === "true" || element.hasAttribute("hidden");
}

function renderedText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? "";
  }

  if (!(node instanceof Element)) {
    return "";
  }

  if (
    isHiddenFromAccessibility(node) ||
    node.localName === "title" ||
    node.localName === "script" ||
    node.localName === "style"
  ) {
    return "";
  }

  return Array.from(node.childNodes, renderedText).join(" ");
}

function labelReferenceIsHidden(referencedElement: HTMLElement, control: HTMLElement): boolean {
  if (isHiddenFromAccessibility(referencedElement)) {
    return true;
  }

  let ancestor = referencedElement.parentElement;
  while (ancestor) {
    if (isHiddenFromAccessibility(ancestor) && !ancestor.contains(control)) {
      return true;
    }
    ancestor = ancestor.parentElement;
  }

  return false;
}

function labelledByReferencesAreValid(element: HTMLElement, labelledBy: string): boolean {
  const ids = labelledBy.trim().split(/\s+/).filter(Boolean);
  if (ids.length === 0) {
    return false;
  }

  return ids.every((id) => {
    const referencedElement = element.ownerDocument.getElementById(id);
    if (!referencedElement || labelReferenceIsHidden(referencedElement, element)) {
      return false;
    }

    return (
      isNonEmptyString(referencedElement.getAttribute("aria-label")) ||
      renderedText(referencedElement).trim().length > 0
    );
  });
}

function hasRenderedAccessibleName(element: HTMLElement): boolean {
  if (isHiddenFromAccessibility(element)) {
    return false;
  }

  if (isNonEmptyString(element.getAttribute("aria-label"))) {
    return true;
  }

  const labelledBy = element.getAttribute("aria-labelledby");
  if (isNonEmptyString(labelledBy) && labelledByReferencesAreValid(element, labelledBy)) {
    return true;
  }

  return renderedText(element).trim().length > 0;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const renderedElementRef = React.useRef<HTMLElement | null>(null);
    const setElementRef = React.useCallback(
      (node: HTMLElement | null) => {
        renderedElementRef.current = node;
        if (typeof ref === "function") {
          ref(node as HTMLButtonElement | null);
        } else if (ref) {
          ref.current = node as HTMLButtonElement | null;
        }
      },
      [ref]
    );

    React.useLayoutEffect(() => {
      if (
        shouldValidateAccessibility &&
        size === "icon" &&
        renderedElementRef.current &&
        !hasRenderedAccessibleName(renderedElementRef.current)
      ) {
        throw new Error(
          'Icon Button requires an accessible name via aria-label, aria-labelledby, or meaningful text.'
        );
      }
    });

    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={setElementRef} {...props}>
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";
